import AmbientGlow from '../components/AmbientGlow';
/**
 * MarketplacePage - Unified storefront across ALL artists.
 * Any active product from any artist's store auto-appears here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Heart, ShoppingBag, Music, Ticket, TrendingUp, Loader2, SlidersHorizontal, Plus, MessageCircle, X, ShoppingCart, Share2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFloatingChat } from '../components/FloatingChatProvider';
import PromoKitModal from '../components/PromoKitModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SORTS = [
  { key: 'default', label: 'For You' },
  { key: 'popular', label: 'Popular' },
  { key: 'newest', label: 'Newest' },
  { key: 'price_low', label: 'Price ↑' },
  { key: 'price_high', label: 'Price ↓' },
];

const CAT_ICON = { music: Music, tickets: Ticket, merch: ShoppingBag };

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { openChat } = useFloatingChat();
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], artists: [], min_price: 0, max_price: 0 });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('default');
  const [category, setCategory] = useState('');
  const [artistId, setArtistId] = useState('');
  const [search, setSearch] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sizePicker, setSizePicker] = useState(null); // { product }
  const [chosenSize, setChosenSize] = useState('');
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailSize, setDetailSize] = useState('');
  const [promoProduct, setPromoProduct] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, limit: '60' });
      if (category) params.set('category', category);
      if (artistId) params.set('artist_id', artistId);
      if (search.trim()) params.set('search', search.trim());
      if (maxPrice) params.set('max_price', maxPrice);
      const res = await fetch(`${API_URL}/api/store/marketplace?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      setProducts(data.products || []);
      if (data.facets) setFacets(data.facets);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [sort, category, artistId, search, maxPrice, token]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const handleVote = async (e, product) => {
    e.stopPropagation();
    if (!token) { navigate('/auth'); return; }
    try {
      const res = await fetch(`${API_URL}/api/store/products/${product.id}/vote`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, vote_count: data.vote_count, has_voted: data.voted } : p));
      }
    } catch (e) {}
  };

  const doBuy = async (product, size = null) => {
    if (!token) { navigate('/auth'); return; }
    setBuyingId(product.id);
    try {
      const body = { origin_url: window.location.origin, product_id: product.id, quantity: 1 };
      if (size) body.size = size;
      const res = await fetch(`${API_URL}/api/store/buy-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setNotice(data.detail || 'Sorry, we couldn’t start checkout. Please try again.');
        setBuyingId(null);
        setTimeout(() => setNotice(null), 4000);
      }
    } catch (e) {
      setNotice('Network error. Please check your connection and try again.');
      setBuyingId(null);
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const handleBuy = (e, product) => {
    e.stopPropagation();
    if (!token) { navigate('/auth'); return; }
    // Apparel with sizes → require a size selection first.
    if (product.sizes && product.sizes.length > 0) {
      setChosenSize('');
      setSizePicker({ product });
      return;
    }
    doBuy(product);
  };

  const fetchCartCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/store/cart`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      const items = data?.cart?.items || [];
      setCartCount(items.reduce((n, it) => n + (it.quantity || 1), 0));
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchCartCount(); }, [fetchCartCount]);

  const addToCart = async (product, size = null) => {
    if (!token) { navigate('/auth'); return; }
    try {
      const res = await fetch(`${API_URL}/api/store/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: product.id, quantity: 1, size: size || null }),
      });
      if (res.ok) {
        setNotice(`Added "${product.name}" to cart`);
        setTimeout(() => setNotice(null), 2500);
        fetchCartCount();
      } else {
        const d = await res.json().catch(() => ({}));
        setNotice(d.detail || 'Could not add to cart');
        setTimeout(() => setNotice(null), 3500);
      }
    } catch {
      setNotice('Network error. Please try again.');
      setTimeout(() => setNotice(null), 3500);
    }
  };


  const messageSeller = (e, product) => {
    e.stopPropagation();
    if (!token) { navigate('/auth'); return; }
    if (user?.id === product.artist_id) { navigate('/store-manager'); return; }
    openChat(
      { id: product.artist_id, name: product.artist_name || 'Artist' },
      `Hi! I've got a question about your product "${product.name}". `,
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images?.[0] || product.image || '',
        artist_id: product.artist_id,
      }
    );
  };

  return (
    <div className="min-h-screen bg-[#050507] pb-24" data-testid="marketplace-page">
      <AmbientGlow />
      {/* Toast notice */}
      {notice && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-[90%] px-4 py-3 rounded-xl bg-[#1a1420] border border-[#FF007F]/40 text-white text-sm shadow-lg shadow-black/40 flex items-center gap-2"
          data-testid="market-buy-error"
          role="alert"
        >
          <span className="w-2 h-2 rounded-full bg-[#FF007F] shrink-0" />
          {notice}
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0B0A10]/90 backdrop-blur-xl border-b border-white/10 px-4 pt-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Marketplace</h1>
            <p className="text-[#A1A1AA] text-sm mt-1">Merch, music & more from every artist</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate(token ? '/store/cart' : '/auth')}
              className="relative w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white transition-colors"
              data-testid="cart-icon-btn"
              title="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF007F] text-white text-[10px] font-bold flex items-center justify-center" data-testid="cart-badge">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate(token ? '/store-manager' : '/auth')}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-sm font-semibold shadow-lg shadow-[#FF007F]/20 active:scale-95 transition-transform"
              data-testid="sell-merch-btn"
            >
              <Plus className="w-4 h-4" /> Sell Merch
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-full px-4 py-2.5">
            <Search className="w-4 h-4 text-[#71717A]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products or artists"
              className="flex-1 bg-transparent text-white text-sm outline-none placeholder-[#52525B]"
              data-testid="marketplace-search"
            />
          </div>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`w-11 h-11 rounded-full flex items-center justify-center ${showFilters ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/5 text-white'}`}
            data-testid="marketplace-filter-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
        </div>

        {/* Sort chips */}
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${sort === s.key ? 'bg-white text-[#050505]' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}
              data-testid={`sort-${s.key}`}
            >
              {s.key === 'popular' && <TrendingUp className="w-3 h-3 inline mr-1" />}
              {s.label}
            </button>
          ))}
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="mt-3 space-y-3 pb-2" data-testid="marketplace-filters">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              <button onClick={() => setCategory('')} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${category === '' ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-[#A1A1AA]'}`} data-testid="cat-all">All categories</button>
              {facets.categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-xs capitalize whitespace-nowrap ${category === c ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-[#A1A1AA]'}`} data-testid={`cat-${c}`}>{c}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <select value={artistId} onChange={(e) => setArtistId(e.target.value)} className="flex-1 bg-white/5 rounded-xl px-3 py-2 text-white text-sm outline-none" data-testid="filter-artist">
                <option value="">All artists</option>
                {facets.artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder={`Max $${facets.max_price || ''}`} className="w-32 bg-white/5 rounded-xl px-3 py-2 text-white text-sm outline-none placeholder-[#52525B]" data-testid="filter-max-price" />
            </div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="px-4 pt-4">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" /></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-[#71717A]" data-testid="marketplace-empty">
            <ShoppingBag className="w-14 h-14 mx-auto mb-3 opacity-40" />
            <p>No products found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.map((p, i) => {
              const Icon = CAT_ICON[p.category] || ShoppingBag;
              const img = (p.images && p.images[0]) || null;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  onClick={() => { setDetailSize(''); setDetailProduct(p); }}
                  className="group bg-[#12101A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00F0FF]/40 transition-colors cursor-pointer"
                  data-testid={`market-item-${p.id}`}
                >
                  <div className="relative aspect-square bg-[#1a1625] overflow-hidden">
                    {img ? (
                      <img src={img} alt={p.name || 'Product'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Icon className="w-10 h-10 text-[#52525B]" /></div>
                    )}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-wider text-white flex items-center gap-1">
                      <Icon className="w-3 h-3" />{p.category || 'merch'}
                    </span>
                    <button
                      onClick={(e) => handleVote(e, p)}
                      className={`absolute top-2 right-2 px-2 py-1 rounded-full backdrop-blur text-xs flex items-center gap-1 transition-colors ${p.has_voted ? 'bg-[#FF007F] text-white' : 'bg-black/60 text-white hover:bg-black/80'}`}
                      data-testid={`vote-btn-${p.id}`}
                    >
                      <Heart className={`w-3 h-3 ${p.has_voted ? 'fill-current' : ''}`} />{p.vote_count || 0}
                    </button>
                  </div>
                  <div className="p-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/profile/${p.artist_id}`); }}
                      className="text-[#00F0FF] text-xs font-medium truncate block hover:underline w-full text-left"
                      data-testid={`market-artist-${p.id}`}
                    >
                      {p.artist_name || 'Artist'}
                    </button>
                    <h3 className="text-white text-sm font-semibold truncate mt-0.5">{p.name || 'Untitled'}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-white font-bold">${Number(p.price || 0).toFixed(2)}</span>
                      <span className="text-[#71717A] text-[11px]">{p.sold_count || 0} sold</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2.5">
                      <button
                        onClick={(e) => handleBuy(e, p)}
                        disabled={buyingId === p.id || (p.stock != null && p.stock <= 0)}
                        className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                        data-testid={`market-buy-${p.id}`}
                      >
                        {buyingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (p.stock != null && p.stock <= 0 ? 'Sold Out' : 'Buy Now')}
                      </button>
                      <button
                        onClick={(e) => messageSeller(e, p)}
                        className="shrink-0 w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                        data-testid={`market-message-${p.id}`}
                        title="Message seller"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </div>
                    {p.sizes && p.sizes.length > 0 && (
                      <p className="text-[#71717A] text-[10px] mt-1.5 text-center">Sizes: {p.sizes.join(' · ')}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Product detail */}
      {detailProduct && (() => {
        const p = detailProduct;
        const Icon = CAT_ICON[p.category] || ShoppingBag;
        const img = (p.images && p.images[0]) || null;
        const soldOut = p.stock != null && p.stock <= 0;
        const needsSize = p.sizes && p.sizes.length > 0;
        const buy = () => { if (needsSize && !detailSize) return; setDetailProduct(null); doBuy(p, detailSize || null); };
        const msg = () => { setDetailProduct(null); messageSeller({ stopPropagation: () => {} }, p); };
        return (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="product-detail-modal">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDetailProduct(null)} />
            <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#12101A] border border-white/10 rounded-t-3xl sm:rounded-3xl">
              <button
                onClick={() => setDetailProduct(null)}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 backdrop-blur text-white/80 hover:text-white"
                data-testid="product-detail-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="relative aspect-square bg-[#1a1625]">
                {img ? (
                  <img src={img} alt={p.name || 'Product'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Icon className="w-16 h-16 text-[#52525B]" /></div>
                )}
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-wider text-white flex items-center gap-1">
                  <Icon className="w-3 h-3" />{p.category || 'merch'}
                </span>
              </div>
              <div className="p-5">
                <button
                  onClick={() => { setDetailProduct(null); navigate(`/profile/${p.artist_id}`); }}
                  className="text-[#00F0FF] text-sm font-medium hover:underline"
                  data-testid="product-detail-artist"
                >
                  {p.artist_name || 'Artist'}
                </button>
                <h2 className="text-white text-xl font-bold mt-1" style={{ fontFamily: 'Unbounded' }}>{p.name || 'Untitled'}</h2>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-white text-2xl font-bold">${Number(p.price || 0).toFixed(2)}</span>
                  <span className="text-[#71717A] text-xs">{p.sold_count || 0} sold{p.stock != null ? ` · ${p.stock} in stock` : ''}</span>
                </div>
                {p.description && (
                  <p className="text-[#A1A1AA] text-sm mt-3 whitespace-pre-line">{p.description}</p>
                )}
                {needsSize && (
                  <div className="mt-4">
                    <p className="text-white text-sm font-medium mb-2">Select a size</p>
                    <div className="flex flex-wrap gap-2">
                      {p.sizes.map((s) => (
                        <button
                          key={s}
                          onClick={() => setDetailSize(s)}
                          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${detailSize === s ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/5 text-white hover:bg-white/10'}`}
                          data-testid={`detail-size-${s.replace(/\s+/g, '-')}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-5">
                  <button
                    onClick={buy}
                    disabled={soldOut || buyingId === p.id || (needsSize && !detailSize)}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="detail-buy"
                  >
                    {buyingId === p.id ? <Loader2 className="w-5 h-5 animate-spin" /> : soldOut ? 'Sold Out' : (needsSize && !detailSize ? 'Select a size' : 'Buy Now')}
                  </button>
                  <button
                    onClick={() => { if (needsSize && !detailSize) return; addToCart(p, detailSize || null); }}
                    disabled={soldOut || (needsSize && !detailSize)}
                    className="shrink-0 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                    data-testid="detail-add-to-cart"
                    title="Add to cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                  </button>
                  <button
                    onClick={msg}
                    className="shrink-0 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center gap-2"
                    data-testid="detail-message"
                  >
                    <MessageCircle className="w-5 h-5" /> Ask
                  </button>
                  <button
                    onClick={() => setPromoProduct(p)}
                    className="shrink-0 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center gap-2"
                    data-testid="detail-promote"
                    title="Promote / Share"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {promoProduct && (
        <PromoKitModal open={!!promoProduct} type="product" id={promoProduct.id} onClose={() => setPromoProduct(null)} />
      )}

      {/* Size picker (apparel) */}
      {sizePicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="size-picker-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSizePicker(null)} />
          <div className="relative w-full sm:max-w-sm bg-[#12101A] border border-white/10 rounded-t-3xl sm:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-white font-bold text-lg">Choose a size</h3>
              <button onClick={() => setSizePicker(null)} className="p-1.5 rounded-full hover:bg-white/10 text-white/60" data-testid="size-picker-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#71717A] text-sm mb-4 truncate">{sizePicker.product.name}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {sizePicker.product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setChosenSize(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${chosenSize === s ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/5 text-white hover:bg-white/10'}`}
                  data-testid={`size-option-${s.replace(/\s+/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={() => { const p = sizePicker.product; const sz = chosenSize; setSizePicker(null); doBuy(p, sz); }}
              disabled={!chosenSize}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-40"
              data-testid="size-picker-confirm"
            >
              {chosenSize ? `Buy Now · ${chosenSize}` : 'Select a size'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;
